import { skipToken, useSuspenseQuery } from "@apollo/client/react";
import { Check, TriangleAlert } from "lucide-react";
import { Suspense, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Badge, Skeleton } from "~/components/ui";
import { ListEffectiveCategoryPropertySchemasDocument } from "~/features/admin/categories/gqls";
import type { CategoryRaw, PropertySchemaRow } from "~/features/admin/categories/types";
import type { InheritedSchemaConflict } from "~/features/admin/categories/utils/schemaConflicts";
import { toInheritedPropertySchemaRows } from "~/features/admin/categories/utils/transformers";
import { useTenant } from "~/features/tenant/context";
import { cn } from "~/lib/utils";

interface InheritedPropertySchemasSectionProps {
  /** Nearest registered ancestor of the parent path, or `null` when the category is a root. */
  sourceCategoryId: string | null;
  categories: readonly CategoryRaw[];
  /** Keys the user is defining on this category — flagged when they shadow an inherited key. */
  ownSchemaNames: readonly string[];
  /** Own rows that shadow an inherited key with a different type/attribute type (parent wins). */
  conflicts: readonly InheritedSchemaConflict[];
  /** Fires whenever the inherited set changes, tagged with the source it belongs to (`null` source → `[]`). */
  onLoaded: (sourceCategoryId: string | null, rows: PropertySchemaRow[]) => void;
}

/**
 * Read-only preview of what the category will inherit from its parent chain.
 * Owns its Suspense boundary so a parent change never unmounts the form.
 */
export function InheritedPropertySchemasSection({
  sourceCategoryId,
  categories,
  ownSchemaNames,
  conflicts,
  onLoaded,
}: InheritedPropertySchemasSectionProps) {
  const { t } = useTranslation();

  return (
    <section className="space-y-2">
      <div>
        <h2 className="text-16-bold capitalize text-fg-1">{t("feature.categories.form.inheritedSchemas.title")}</h2>
        <p className="text-12-regular text-fg-3">{t("feature.categories.form.inheritedSchemas.description")}</p>
      </div>
      {sourceCategoryId ? (
        <Suspense fallback={<Skeleton className="h-16 w-full" />}>
          <InheritedRows
            key={sourceCategoryId}
            sourceCategoryId={sourceCategoryId}
            categories={categories}
            ownSchemaNames={ownSchemaNames}
            conflicts={conflicts}
            onLoaded={onLoaded}
          />
        </Suspense>
      ) : (
        <NoSource onLoaded={onLoaded} />
      )}
    </section>
  );
}

function NoSource({ onLoaded }: Pick<InheritedPropertySchemasSectionProps, "onLoaded">) {
  const { t } = useTranslation();
  useEffect(() => {
    onLoaded(null, []);
  }, [onLoaded]);
  return <p className="text-14-regular text-fg-3">{t("feature.categories.form.inheritedSchemas.none")}</p>;
}

type InheritedRowsProps = Omit<InheritedPropertySchemasSectionProps, "sourceCategoryId"> & { sourceCategoryId: string };

function InheritedRows({ sourceCategoryId, categories, ownSchemaNames, conflicts, onLoaded }: InheritedRowsProps) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  // errorPolicy "all": a failed read must degrade to a notice inside this section. Throwing would reach the
  // app-level error boundary and wipe the half-filled form; it must also still report (empty) rows so Save unlocks.
  const { data, dataState, error } = useSuspenseQuery(
    ListEffectiveCategoryPropertySchemasDocument,
    tenantId ? { variables: { tenantId, categoryId: sourceCategoryId }, errorPolicy: "all" } : skipToken,
  );

  // Keyed on the payload itself (stable in the Apollo cache), not the per-render result wrapper.
  const effective = tenantId && dataState === "complete" ? data?.cnutListEffectiveCategoryPropertySchemas : undefined;
  const rows = useMemo(
    () => (effective ? toInheritedPropertySchemaRows(effective, categories) : []),
    [effective, categories],
  );
  useEffect(() => {
    onLoaded(sourceCategoryId, rows);
  }, [sourceCategoryId, rows, onLoaded]);

  if (error || !effective) {
    return (
      <p className="inline-flex items-center gap-1 text-14-regular text-danger">
        <TriangleAlert aria-hidden className="h-3.5 w-3.5" />
        {t("feature.categories.form.inheritedSchemas.loadFailed")}
      </p>
    );
  }

  if (rows.length === 0) {
    return <p className="text-14-regular text-fg-3">{t("feature.categories.form.inheritedSchemas.empty")}</p>;
  }

  const conflictByName = new Map(conflicts.map((conflict) => [conflict.name, conflict]));

  return (
    <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default bg-bg-subtle">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <Th>{t("model.common.name")}</Th>
            <Th>{t("model.propertySchema.propertyTypeId")}</Th>
            <Th className="text-center">{t("model.propertySchemaAssociation.required")}</Th>
            <Th className="text-center">{t("model.propertySchemaAssociation.unique")}</Th>
            <Th>{t("model.propertySchemaAssociation.propertyAttributeTypeId")}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const conflict = conflictByName.get(row.name);
            const redefined = !conflict && ownSchemaNames.includes(row.name);
            return (
              <tr key={row.id} className="border-t border-border-default">
                <Td className="font-medium text-fg-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span>{row.name}</span>
                    <span className="text-12-regular text-fg-3">
                      {t("feature.categories.form.inheritedSchemas.from", { category: row.sourceCategoryName })}
                    </span>
                    {conflict && (
                      <span className="inline-flex items-center gap-1 text-12-medium text-danger">
                        <TriangleAlert aria-hidden className="h-3.5 w-3.5" />
                        {t("feature.categories.form.inheritedSchemas.conflict", {
                          own: describeSchema(conflict.own.propertyTypeId, conflict.own.propertyAttributeTypeId),
                          inherited: describeSchema(row.propertyTypeId, row.propertyAttributeTypeId),
                        })}
                      </span>
                    )}
                    {redefined && (
                      <Badge tone="default" className="font-normal">
                        {t("feature.categories.form.inheritedSchemas.redefined")}
                      </Badge>
                    )}
                  </div>
                </Td>
                <Td>
                  <Badge tone="default">{row.propertyTypeId}</Badge>
                </Td>
                <Td className="text-center">
                  {row.required && <Check className="mx-auto h-3.5 w-3.5 text-success" />}
                </Td>
                <Td className="text-center">{row.unique && <Check className="mx-auto h-3.5 w-3.5 text-success" />}</Td>
                <Td>
                  {row.propertyAttributeTypeId ? <Badge tone="default">{row.propertyAttributeTypeId}</Badge> : null}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** "string" or "string@title" — compact type + attribute description for conflict messages. */
export function describeSchema(propertyTypeId: string, propertyAttributeTypeId: string | null): string {
  return propertyAttributeTypeId ? `${propertyTypeId}@${propertyAttributeTypeId}` : propertyTypeId;
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th className={cn("px-3 py-2 text-left text-10-regular uppercase tracking-[0.06em] text-fg-2", className)}>
      {children}
    </th>
  );
}

function Td({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <td className={cn("px-3 py-2 text-14-regular align-middle", className)}>{children}</td>;
}
